import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Heart, Users, Calendar, Sparkles, Clock, DollarSign, AlertTriangle, UserMinus, Lock, Headphones, ShieldCheck, Laptop, FileCheck, ChevronDown, ArrowRight, Star, Award, TrendingUp, Quote } from 'lucide-react';
import ConsultationForm from '../components/ConsultationForm';
import NAOExitPopup from '../components/NAOExitPopup';
import CookieConsent from '../components/CookieConsent';
import { credentialBadges } from '../constants/credentialBadges';
import '../styles/nao.css';

const NAOPage = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitPopupShown, setExitPopupShown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 600);
    };

    let pageLoadTime = Date.now();
    const handleMouseLeave = (e) => {
      const timeOnPage = Date.now() - pageLoadTime;
      if (e.clientY <= 0 && timeOnPage > 5000 && !exitPopupShown && !showThankYou && !showExitPopup) {
        setShowExitPopup(true);
        setExitPopupShown(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [showThankYou, exitPopupShown, showExitPopup]);

  const handleFormSubmit = (email) => {
    setSubmittedEmail(email);
    setShowThankYou(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToForm = () => {
    document.getElementById('nao-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const openCalendly = () => {
    window.open('https://calendly.com/jlatwesen', '_blank');
  };

  if (showThankYou) {
    return (
      <div className="msa-page">
        <ThankYouSection email={submittedEmail} />
        <MSAFooter credentialBadges={credentialBadges} />
      </div>
    );
  }

  return (
    <div className="msa-page">
      <Helmet>
        <title>MSA Member PEO Partnership | Amplify HR</title>
        <meta name="description" content="Exclusive PEO services for MSA members—access affordable health benefits, streamlined payroll, and dedicated HR support designed for mission-driven organizations" />
      </Helmet>
      
      <NAOExitPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)}
        onSubmit={() => setShowExitPopup(false)}
      />

      {showStickyCTA && (
        <div className="msa-sticky-bar">
          <div className="msa-sticky-content">
            <span>MSA Member Exclusive Pricing</span>
            <button onClick={scrollToForm} data-testid="sticky-cta-btn">Get Free Analysis</button>
          </div>
        </div>
      )}

      <HeaderStrip />
      <HeroSection onFormSubmit={handleFormSubmit} openCalendly={openCalendly} />
      <MissionStatement />
      <PartnershipManagerSection openCalendly={openCalendly} />
      <JourneySection />
      <SolutionsSection />
      <SocialProofSection />
      <FAQSection />
      <FinalCTASection onFormSubmit={handleFormSubmit} openCalendly={openCalendly} />
      <MSAFooter credentialBadges={credentialBadges} />
      <CookieConsent />
    </div>
  );
};

// ============================================
// HEADER STRIP - Logos
// ============================================
const HeaderStrip = () => (
  <div className="msa-header-strip">
    <div className="msa-header-inner">
      <div className="msa-logos">
        <a href="https://amplifyhrm.com" target="_blank" rel="noopener noreferrer">
          <img 
            src="https://customer-assets.emergentagent.com/job_69a9ffbf-3b0d-4791-80d1-6eb6aacb68ee/artifacts/5dacw0dj_Amplify%20-%20Logo.jpg" 
            alt="Amplify HR Logo"
            className="msa-logo-amplify"
          />
        </a>
        <div className="msa-logo-divider"></div>
        <img 
          src="https://customer-assets.emergentagent.com/job_2bac5358-3276-4771-b6d1-4470e68b545a/artifacts/j79fhi1i_MSA%20Logo_Final_Green.jpg" 
          alt="Mission Service Allies Logo"
          className="msa-logo-msa"
        />
      </div>
      <img 
        src="https://crowdstake-website-images-prod.s3.us-east-2.amazonaws.com/user-81cb0550-3031-701e-31f9-b6f078c381e0/878aa211-b635-4c12-813e-aa2b27a5743c_optimized.webp" 
        alt="IRS Certified PEO"
        className="msa-logo-irs"
      />
    </div>
  </div>
);

// ============================================
// HERO SECTION - Keep existing layout (Announcement + Form)
// ============================================
const HeroSection = ({ onFormSubmit, openCalendly }) => (
  <section className="msa-hero">
    <div className="msa-hero-bg"></div>
    <div className="msa-hero-container">
      <div className="msa-hero-grid">
        {/* Left Side - Announcement */}
        <div className="msa-hero-left">
          <div className="msa-hero-headline-area" data-testid="msa-exclusive-card">
            <span className="msa-exclusive-pill">
              <Sparkles size={14} />
              MSA Member Exclusive
            </span>
            <h1>MSA × Amplify HR</h1>
            <p className="msa-hero-tagline">Exclusive PEO partner for Mission Service Allies members</p>
          </div>
          
          <div className="msa-announcement">
            <div className="msa-announcement-label">
              <Sparkles size={14} />
              <span>Partnership Announcement</span>
            </div>
            <p>
              The <strong>Nonprofit Association of Oregon</strong> has established 
              <strong> Mission Service Allies (MSA)</strong>, a charitable LLC providing back-office 
              services to nonprofit members. MSA is partnering with <strong>Amplify HR</strong> to offer 
              PEO services with <strong>heavily discounted rates</strong>.
            </p>
            <div className="msa-announcement-note">
              <CheckCircle2 size={16} />
              <span>NAO members are automatically MSA members with special pricing.</span>
            </div>
          </div>
          
          <button onClick={openCalendly} className="msa-schedule-btn" data-testid="hero-schedule-btn">
            <Calendar size={18} />
            Schedule a Meeting Now
          </button>
        </div>
        
        {/* Right Side - Form */}
        <div className="msa-hero-right" id="nao-form">
          <div className="msa-form-card" data-testid="consultation-form-card">
            <div className="msa-form-badges">
              <span><CheckCircle2 size={14} /> Certified</span>
              <span><CheckCircle2 size={14} /> Accredited</span>
              <span><CheckCircle2 size={14} /> Award Winning</span>
            </div>
            <h3>Get Your Free Analysis</h3>
            <ConsultationForm onSubmit={onFormSubmit} source="nao" />
            <p className="msa-form-note"><Lock size={12} /> No obligation.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// MISSION STATEMENT - Warm, Human intro
// ============================================
const MissionStatement = () => (
  <section className="msa-mission">
    <div className="msa-mission-container">
      <h1>Focus on Your Mission.<br/>We'll Handle HR.</h1>
      <p className="msa-mission-text">
        You started your nonprofit to make a difference—not to navigate payroll taxes, 
        compliance regulations, and benefits administration. As Oregon's exclusive PEO 
        partner for MSA members, we give you back the time and resources to do what matters most.
      </p>
      <div className="msa-mission-stats">
        <div className="msa-stat">
          <span className="msa-stat-number">15+</span>
          <span className="msa-stat-label">hours/week saved on HR admin</span>
        </div>
        <div className="msa-stat-divider"></div>
        <div className="msa-stat">
          <span className="msa-stat-number">$72k</span>
          <span className="msa-stat-label">average annual savings</span>
        </div>
        <div className="msa-stat-divider"></div>
        <div className="msa-stat">
          <span className="msa-stat-number">97%</span>
          <span className="msa-stat-label">client satisfaction</span>
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// JOURNEY SECTION - "Sound Familiar?" as a story
// ============================================
const JourneySection = () => (
  <section className="msa-journey">
    <div className="msa-journey-container">
      <div className="msa-journey-intro">
        <span className="msa-section-label">Sound Familiar?</span>
        <h2>The nonprofit leader's HR journey</h2>
        <p>Every dollar spent fighting HR fires is a dollar that could serve your community.</p>
      </div>
      
      <div className="msa-journey-timeline">
        <div className="msa-journey-line"></div>
        
        <div className="msa-journey-item" data-testid="journey-item-1">
          <div className="msa-journey-dot"><Clock size={16} /></div>
          <div className="msa-journey-content">
            <span className="msa-journey-quote">"I spent all week on payroll issues instead of our community programs..."</span>
            <p>Time that should go to your mission gets consumed by administrative tasks.</p>
          </div>
        </div>
        
        <div className="msa-journey-item" data-testid="journey-item-2">
          <div className="msa-journey-dot"><DollarSign size={16} /></div>
          <div className="msa-journey-content">
            <span className="msa-journey-quote">"We lost another great candidate because we couldn't match their benefits..."</span>
            <p>Without competitive benefits, attracting talent becomes an uphill battle.</p>
          </div>
        </div>
        
        <div className="msa-journey-item" data-testid="journey-item-3">
          <div className="msa-journey-dot"><AlertTriangle size={16} /></div>
          <div className="msa-journey-content">
            <span className="msa-journey-quote">"Did employment laws change again? I can't keep up..."</span>
            <p>Compliance complexity grows while resources stay limited.</p>
          </div>
        </div>
        
        <div className="msa-journey-item" data-testid="journey-item-4">
          <div className="msa-journey-dot"><UserMinus size={16} /></div>
          <div className="msa-journey-content">
            <span className="msa-journey-quote">"Another resignation letter. That's the third this quarter..."</span>
            <p>High turnover drains energy and institutional knowledge.</p>
          </div>
        </div>
      </div>
      
      <div className="msa-journey-transition">
        <ArrowRight size={24} />
        <span>There's a better way</span>
      </div>
    </div>
  </section>
);

// ============================================
// SOLUTIONS SECTION - What we offer
// ============================================
const SolutionsSection = () => (
  <section className="msa-solutions">
    <div className="msa-solutions-container">
      <span className="msa-section-label">The Amplify Advantage</span>
      <h2>Everything you need. Nothing you don't.</h2>
      
      <div className="msa-solutions-flow">
        <div className="msa-solution-card" data-testid="solution-payroll">
          <div className="msa-solution-icon"><Users size={28} /></div>
          <h3>Payroll & Benefits</h3>
          <p>Competitive health plans, retirement options, and seamless payroll—all managed for you.</p>
          <ul>
            <li>$0 deductible health plans available</li>
            <li>401(k) with employer match options</li>
            <li>Pay-as-you-go workers' comp</li>
          </ul>
        </div>
        
        <div className="msa-solution-connector"></div>
        
        <div className="msa-solution-card" data-testid="solution-compliance">
          <div className="msa-solution-icon"><ShieldCheck size={28} /></div>
          <h3>Compliance & Risk</h3>
          <p>Stay ahead of employment law changes. We handle the complexity so you don't have to.</p>
          <ul>
            <li>$1M EPLI coverage included</li>
            <li>Proactive compliance monitoring</li>
            <li>HR policy development</li>
          </ul>
        </div>
        
        <div className="msa-solution-connector"></div>
        
        <div className="msa-solution-card" data-testid="solution-support">
          <div className="msa-solution-icon"><Headphones size={28} /></div>
          <h3>Dedicated Support</h3>
          <p>Your own team of HR experts who understand nonprofits—available when you need them.</p>
          <ul>
            <li>Dedicated account manager</li>
            <li>Grant-compliant reporting</li>
            <li>Board presentation support</li>
          </ul>
        </div>
      </div>
      
      <div className="msa-solutions-extras">
        <span><Laptop size={16} /> HR Technology</span>
        <span><FileCheck size={16} /> Onboarding</span>
        <span><Heart size={16} /> Employee Relations</span>
      </div>
    </div>
  </section>
);

// ============================================
// PARTNERSHIP MANAGER - Jenny Latwesen
// ============================================
const PartnershipManagerSection = ({ openCalendly }) => (
  <section className="msa-partner">
    <div className="msa-partner-container">
      <div className="msa-partner-content">
        <span className="msa-section-label">Your Dedicated Partner</span>
        <h2>Meet Jenny Latwesen</h2>
        <p className="msa-partner-title">MSA Channel & Partnership Manager</p>
        <p className="msa-partner-desc">
          As your dedicated point of contact, Jenny specializes in helping nonprofits 
          navigate HR challenges. She understands the unique needs of 501(c)(3) organizations 
          and will personally guide you through every step of the process.
        </p>
        <button onClick={openCalendly} className="msa-partner-btn" data-testid="partner-schedule-btn">
          <Calendar size={18} />
          Schedule a Meeting with Jenny
        </button>
      </div>
      
      <div className="msa-partner-benefits">
        <h4>Exclusive MSA Member Benefits</h4>
        <div className="msa-benefit-list">
          <div className="msa-benefit">
            <DollarSign size={18} />
            <div>
              <strong>Preferred Pricing</strong>
              <span>Special rates only for MSA members</span>
            </div>
          </div>
          <div className="msa-benefit">
            <TrendingUp size={18} />
            <div>
              <strong>Financial Modeling</strong>
              <span>Custom budget projections</span>
            </div>
          </div>
          <div className="msa-benefit">
            <Users size={18} />
            <div>
              <strong>Board Support</strong>
              <span>Presentation-ready materials</span>
            </div>
          </div>
          <div className="msa-benefit">
            <Award size={18} />
            <div>
              <strong>Nonprofit Expertise</strong>
              <span>501(c)(3) specialists</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// SOCIAL PROOF - Trust indicators
// ============================================
const SocialProofSection = () => (
  <section className="msa-proof">
    <div className="msa-proof-container">
      <div className="msa-proof-trust">
        <h3>Why nonprofits trust Amplify</h3>
        <ul>
          <li><CheckCircle2 size={18} /> IRS Certified PEO (CPEO) — highest federal standard</li>
          <li><CheckCircle2 size={18} /> ESAC Accredited — rigorous third-party verification</li>
          <li><CheckCircle2 size={18} /> 4x Inc. 5000 honoree — proven, sustainable growth</li>
          <li><CheckCircle2 size={18} /> 97% client satisfaction on transitions</li>
        </ul>
      </div>
      
      <div className="msa-proof-testimonials">
        <div className="msa-testimonial" data-testid="testimonial-1">
          <Quote size={24} className="msa-quote-icon" />
          <p>"I would highly recommend Amplify HR to any organization seeking a professional and reliable HR partner. Their team expertly manages all the day-to-day functions of an enterprise-level HR department, allowing us to focus on our core business."</p>
          <cite>
            <strong>Y.B.</strong>
            <span>Executive Director</span>
          </cite>
        </div>
        
        <div className="msa-testimonial-small-group">
          <div className="msa-testimonial-small">
            <p>"Better insurance prices, lower admin fees and superior service."</p>
            <cite>— T.E., President</cite>
          </div>
          <div className="msa-testimonial-small">
            <p>"They help reduce our administrative burdens while providing excellent service."</p>
            <cite>— A.Z., Co-Owner</cite>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// FAQ SECTION
// ============================================
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: "How much does it cost?",
      a: "Most nonprofits see savings on total employment costs after switching. Your free cost analysis evaluates hard costs, time costs, and soft costs to show the projected impact based on your organization."
    },
    {
      q: "Will we lose control over our employees?",
      a: "No. You keep full authority over hiring, supervision, job duties, and day-to-day management. Amplify HR handles the HR administration, payroll processing, benefits administration, and compliance support."
    },
    {
      q: "What is the impact on our 501(c)(3) status?",
      a: "None. The co-employment model has zero impact on your tax-exempt status."
    },
    {
      q: "Can we keep our current benefits?",
      a: "Yes. You can keep your current plans, adopt Amplify plans like FirstAdvantage™, or choose a hybrid approach that fits your team and budget."
    },
    {
      q: "How long does it take to get started?",
      a: "Implementation typically takes 30-45 days. We prioritize ensuring your staff have a positive experience and 97% of clients report the transition is efficient and non-disruptive."
    }
  ];

  return (
    <section className="msa-faq">
      <div className="msa-faq-container">
        <h2>Common Questions</h2>
        <div className="msa-faq-list">
          {faqs.map((faq, idx) => (
            <div 
              key={faq.q} 
              className={`msa-faq-item ${openIndex === idx ? 'open' : ''}`}
              data-testid={`faq-item-${idx}`}
            >
              <button 
                className="msa-faq-question"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              >
                {faq.q}
                <ChevronDown size={20} />
              </button>
              {openIndex === idx && (
                <div className="msa-faq-answer">
                  <p>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FINAL CTA
// ============================================
const FinalCTASection = ({ onFormSubmit, openCalendly }) => (
  <section className="msa-final">
    <div className="msa-final-container">
      <div className="msa-final-content">
        <h2>Make Every Dollar Go Further</h2>
        <p>
          When you reduce HR costs by 15% to 40%, you free up resources for mission work. 
          Get your free cost analysis showing exactly how much your nonprofit can reinvest 
          in programs and services. MSA member pricing included.
        </p>
        <button onClick={openCalendly} className="msa-final-schedule" data-testid="final-schedule-btn">
          <Calendar size={18} />
          Schedule a Meeting Now
        </button>
      </div>
      <div className="msa-final-form">
        <ConsultationForm onSubmit={onFormSubmit} source="nao" />
      </div>
    </div>
  </section>
);

// ============================================
// THANK YOU
// ============================================
const ThankYouSection = ({ email }) => (
  <section className="msa-thankyou">
    <div className="msa-thankyou-container">
      <div className="msa-thankyou-icon">
        <CheckCircle2 size={64} />
      </div>
      <h1>Thank You for Your Request!</h1>
      <p>Your dedicated specialist will be reaching out shortly. Check your email for more details and next steps.</p>
    </div>
  </section>
);

// ============================================
// FOOTER
// ============================================
const MSAFooter = ({ credentialBadges }) => (
  <footer className="msa-footer">
    <div className="msa-footer-badges">
      {credentialBadges.map((badge) => (
        <div key={badge.alt} className="msa-footer-badge">
          <img src={badge.src} alt={badge.alt} loading="lazy" />
        </div>
      ))}
    </div>
    <p>© 2026 Amplify HR. Exclusive PEO partner for Mission Service Allies members.</p>
  </footer>
);

export default NAOPage;
